import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, signOut } from 'firebase/auth';
import { collection, query, orderBy, onSnapshot, doc, setDoc, increment } from 'firebase/firestore';
import { QRCodeSVG } from 'qrcode.react';
import { auth, db } from './firebase';
import { Receipt, Plus, Copy, ChevronRight, LogOut, Users, ShieldCheck, QrCode, X, CalendarDays, MapPin } from 'lucide-react';
import './GroupEventDashboard.css';

interface GroupEvent {
  id: string;
  name: string;
  venue: string;
  eventDate: number;
  eventCode: string;
  totalMembers: number;
  contributors: { items: { amount: number; isGroup: boolean }[] }[];
}

function groupTotal(event: GroupEvent): number {
  return event.contributors.reduce(
    (sum, c) => sum + c.items.filter(i => i.isGroup).reduce((s, i) => s + i.amount, 0),
    0
  );
}

function perPersonShare(event: GroupEvent): number {
  return event.totalMembers > 0 ? groupTotal(event) / event.totalMembers : 0;
}

function formatDate(ms: number) {
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function GroupEventDashboard({ user, role }: { user: User; role: string }) {
  const [events, setEvents] = useState<GroupEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);
  const [qrEvent, setQrEvent] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setDoc(doc(db, 'analytics', 'siteStats'), { adminPageViews: increment(1) }, { merge: true })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const q = query(
      collection(db, 'users', user.uid, 'events'),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snap) => {
      setEvents(snap.docs.map(d => d.data() as GroupEvent));
      setLoading(false);
    }, () => setLoading(false));
  }, [user.uid]);

  function copyLink(code: string) {
    navigator.clipboard.writeText(`https://xpensemate.com/event/${code}`);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  }

  function toggleQR(code: string) {
    setQrEvent(prev => prev === code ? null : code);
  }

  return (
    <div className="gd-page">
      <header className="gd-header">
        <div className="gd-logo">
          <div className="gd-logo-icon">
            <Receipt size={18} color="white" strokeWidth={2} />
          </div>
          <span className="gd-logo-text">XpenseMate</span>
        </div>
        <div className="gd-header-right">
          {role === 'super_admin' && (
            <button
              className="gd-btn-super"
              onClick={() => navigate('/super-admin-setup')}
              title="Super Admin"
            >
              <ShieldCheck size={15} /> Super Admin
            </button>
          )}
          <button className="gd-btn-new" onClick={() => navigate('/create')}>
            <Plus size={15} /> New Event
          </button>
          <div className="gd-user-pill">
            <img src={user.photoURL || ''} alt="" className="gd-avatar" />
            <span className="gd-user-name">{user.displayName?.split(' ')[0]}</span>
            <button className="gd-signout" onClick={() => signOut(auth)} title="Sign out">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>

      <main className="gd-main">
        {loading ? (
          <div className="gd-center"><div className="gd-spinner" /></div>
        ) : events.length === 0 ? (
          <div className="gd-empty">
            <div className="gd-empty-icon">
              <Users size={44} strokeWidth={1.2} />
            </div>
            <h2>No events yet</h2>
            <p>Create your first event and share the link with your group.</p>
            <button className="gd-btn-primary" onClick={() => navigate('/create')}>
              <Plus size={16} /> Create your first event
            </button>
          </div>
        ) : (
          <>
            <div className="gd-page-header">
              <h1 className="gd-page-title">Your Events</h1>
              <span className="gd-event-count">{events.length}</span>
            </div>
            <div className="gd-list">
              {events.map(event => {
                const submitted = event.contributors.length;
                const pps = perPersonShare(event);
                const pct = Math.min(100, (submitted / event.totalMembers) * 100);
                const isQROpen = qrEvent === event.eventCode;

                return (
                  <div key={event.id} className="gd-card">
                    <div className="gd-card-body">
                      <div className="gd-card-top">
                        <div className="gd-event-icon">
                          <Users size={16} strokeWidth={1.8} />
                        </div>
                        <div className="gd-event-info">
                          <div className="gd-event-name-row">
                            <h3 className="gd-event-name">{event.name}</h3>
                            <span className="gd-code-badge">{event.eventCode}</span>
                          </div>
                          <div className="gd-event-meta">
                            {event.venue && (
                              <>
                                <MapPin size={11} className="gd-meta-icon" />
                                <span>{event.venue}</span>
                                <span className="gd-sep">·</span>
                              </>
                            )}
                            <CalendarDays size={11} className="gd-meta-icon" />
                            <span>{formatDate(event.eventDate)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="gd-stats-row">
                        <div className="gd-stat-block">
                          <span className="gd-stat-val">{submitted}<span className="gd-stat-denom">/{event.totalMembers}</span></span>
                          <span className="gd-stat-lbl">submitted</span>
                        </div>
                        <div className="gd-stat-divider" />
                        <div className="gd-stat-block accent">
                          <span className="gd-stat-val">${pps.toFixed(2)}</span>
                          <span className="gd-stat-lbl">per person</span>
                        </div>
                        <div className="gd-stat-divider" />
                        <div className="gd-stat-block">
                          <span className="gd-stat-val">{Math.round(pct)}<span className="gd-stat-denom">%</span></span>
                          <span className="gd-stat-lbl">complete</span>
                        </div>
                      </div>

                      <div className="gd-progress-track">
                        <div className="gd-progress-fill" style={{ width: `${pct}%` }} />
                      </div>
                    </div>

                    {isQROpen && (
                      <div className="gd-qr-panel">
                        <button className="gd-qr-close" onClick={() => setQrEvent(null)}>
                          <X size={16} />
                        </button>
                        <QRCodeSVG
                          value={`https://xpensemate.com/event/${event.eventCode}`}
                          size={176}
                          bgColor="#161616"
                          fgColor="#ffffff"
                          level="M"
                        />
                        <p className="gd-qr-hint">Members scan to submit expenses</p>
                        <p className="gd-qr-url">xpensemate.com/event/{event.eventCode}</p>
                      </div>
                    )}

                    <div className="gd-card-actions">
                      <button className="gd-btn-action" onClick={() => copyLink(event.eventCode)}>
                        <Copy size={13} />
                        {copied === event.eventCode ? 'Copied!' : 'Copy link'}
                      </button>
                      <button
                        className={`gd-btn-action${isQROpen ? ' active' : ''}`}
                        onClick={() => toggleQR(event.eventCode)}
                      >
                        <QrCode size={13} /> QR Code
                      </button>
                      <button
                        className="gd-btn-action primary"
                        onClick={() => navigate(`/admin/event/${event.eventCode}`)}
                      >
                        View <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
